import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stack,
  Avatar
} from '@mui/material';
import {
  NotificationsActive,
  VideoCall,
  CheckCircle
} from '@mui/icons-material';
import { keyframes } from '@mui/system';
import { useAuth } from '../context/AuthContext';
import socketService from '../services/socket';
import { ringAPI } from '../services/api';
import ringtone from '../assets/sounds/ringtone.mp3';

// Each ring plays the tone for at most 59 seconds, then auto-silences.
const RING_AUDIBLE_MS = 59 * 1000;

const pulse = keyframes`
  0%   { transform: scale(1);   box-shadow: 0 0 0 0 rgba(211,47,47,0.55); }
  70%  { transform: scale(1.06); box-shadow: 0 0 0 26px rgba(211,47,47,0); }
  100% { transform: scale(1);   box-shadow: 0 0 0 0 rgba(211,47,47,0); }
`;

/**
 * Global incoming-ring overlay. Mounted once (App.js) so it works on any page.
 * Listens for `ring_incoming` / `ring_cancelled`, plays the ringtone once (59s
 * cap), and restores any in-progress ring after a refresh/reconnect via
 * GET /api/ring/active.
 */
const RingManager = () => {
  const { isAuthenticated } = useAuth();
  const [ring, setRing] = useState(null); // active RingAlert (payload)
  const [acknowledging, setAcknowledging] = useState(false);
  const audioRef = useRef(null);
  const silenceTimer = useRef(null);
  const listenerSetup = useRef(false);

  const stopAudio = useCallback(() => {
    if (silenceTimer.current) {
      clearTimeout(silenceTimer.current);
      silenceTimer.current = null;
    }
    const el = audioRef.current;
    if (el) {
      try {
        el.pause();
        el.currentTime = 0;
      } catch (e) {
        /* no-op */
      }
    }
  }, []);

  // Start (or restart) the 59-second audible ring.
  const startAudio = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    try {
      el.currentTime = 0;
      const p = el.play();
      if (p && p.catch) {
        p.catch((err) => console.log('🔔 Ring audio blocked/failed:', err?.message));
      }
    } catch (e) {
      /* no-op */
    }
    if (silenceTimer.current) clearTimeout(silenceTimer.current);
    silenceTimer.current = setTimeout(() => {
      // Auto-silence after 59s; the alert stays on screen until acknowledged.
      const a = audioRef.current;
      if (a) {
        try {
          a.pause();
          a.currentTime = 0;
        } catch (e) {
          /* no-op */
        }
      }
    }, RING_AUDIBLE_MS);
  }, []);

  const showRing = useCallback(
    (payload) => {
      if (!payload) return;
      setRing(payload);
      startAudio();
    },
    [startAudio]
  );

  const clearRing = useCallback(() => {
    stopAudio();
    setRing(null);
  }, [stopAudio]);

  // Restore any active ring on mount / when auth becomes ready.
  useEffect(() => {
    if (!isAuthenticated) {
      clearRing();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await ringAPI.active();
        const alerts = res?.data?.alerts || [];
        if (!cancelled && alerts.length > 0) {
          showRing(alerts[0]);
        }
      } catch (e) {
        /* ignore — nothing to restore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, showRing, clearRing]);

  // Wire up socket listeners (retry until the shared socket exists).
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    const handleIncoming = (payload) => showRing(payload);
    const handleCancelled = (data) => {
      setRing((cur) => {
        if (cur && data?.alertId && String(cur._id) === String(data.alertId)) {
          stopAudio();
          return null;
        }
        if (cur && !data?.alertId) {
          stopAudio();
          return null;
        }
        return cur;
      });
    };

    const setup = () => {
      const socket = socketService.getSocket();
      if (!socket) return false;
      if (listenerSetup.current) return true;
      socket.on('ring_incoming', handleIncoming);
      socket.on('ring_cancelled', handleCancelled);
      listenerSetup.current = true;
      return true;
    };

    let interval = null;
    if (!setup()) {
      let attempts = 0;
      interval = setInterval(() => {
        attempts += 1;
        if (setup() || attempts >= 20) clearInterval(interval);
      }, 500);
    }

    return () => {
      if (interval) clearInterval(interval);
      const socket = socketService.getSocket();
      if (socket) {
        socket.off('ring_incoming', handleIncoming);
        socket.off('ring_cancelled', handleCancelled);
      }
      listenerSetup.current = false;
    };
  }, [isAuthenticated, showRing, stopAudio]);

  // Stop audio if the component ever unmounts.
  useEffect(() => stopAudio, [stopAudio]);

  const acknowledge = useCallback(
    async (thenJoin = false) => {
      if (!ring) return;
      setAcknowledging(true);
      try {
        await ringAPI.acknowledge(ring._id);
      } catch (e) {
        // Even if the ack call fails (e.g. offline), silence locally.
        console.log('Ring acknowledge failed:', e?.message);
      } finally {
        const link = ring.meetingLink;
        clearRing();
        setAcknowledging(false);
        if (thenJoin && link) {
          window.open(link, '_blank', 'noopener');
        }
      }
    },
    [ring, clearRing]
  );

  const targetLabel =
    ring?.fromUserType === 'student'
      ? 'A student is calling you to the class'
      : ring?.fromUserType === 'teacher'
      ? 'Your teacher is calling you to the class'
      : ring?.fromUserType === 'admin'
      ? 'EduMetrix is calling you to the class'
      : 'You are being called to the class';

  return (
    <>
      {/* Hidden audio element for the ringtone */}
      <audio ref={audioRef} src={ringtone} preload="auto" />

      <Dialog
        open={Boolean(ring)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            textAlign: 'center'
          }
        }}
      >
        <DialogContent sx={{ py: 5, px: 4 }}>
          <Box
            sx={{
              width: 96,
              height: 96,
              mx: 'auto',
              mb: 3,
              borderRadius: '50%',
              bgcolor: 'error.main',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: `${pulse} 1.4s infinite`
            }}
          >
            <NotificationsActive sx={{ fontSize: 48 }} />
          </Box>

          <Typography variant="overline" color="error" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            Incoming Class Ring
          </Typography>

          <Typography variant="h6" sx={{ mt: 1, fontWeight: 700 }}>
            {ring?.fromName || 'EduMetrix'}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {targetLabel}
          </Typography>

          {ring?.subject && (
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: 12 }}>
                {ring.subject.charAt(0)}
              </Avatar>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {ring.subject}
                {ring.attemptNumber > 1 ? `  ·  Reminder #${ring.attemptNumber}` : ''}
              </Typography>
            </Stack>
          )}

          <Stack spacing={1.5} sx={{ mt: 4 }}>
            {ring?.meetingLink && (
              <Button
                variant="contained"
                color="success"
                size="large"
                startIcon={<VideoCall />}
                disabled={acknowledging}
                onClick={() => acknowledge(true)}
              >
                Join Class Now
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              size="large"
              startIcon={<CheckCircle />}
              disabled={acknowledging}
              onClick={() => acknowledge(false)}
            >
              Acknowledge & Silence
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RingManager;
